import React, { useState, useEffect, useRef } from "react";
import "./ysentinalOne.scss";
import useTheme from "hooks/observer";
import useScreenMobile from "hooks/useScreen";
import LogHistorySidebar from "./sidebar/RightSideBar";
import FooterSearchBar2 from "./Footer/FooterSearchBar2";
import { sendQuery } from "@induz/sdk";
import { StorageDistribution } from "./data/queuedSdk";
import TableViews from "./Table1/TableViews";
import SuggestedCharts from "./SuggestedCharts/SuggestedCharts";
import ChartDashboard from "./SuggestedCharts/ChartDashboard";
import LoadingSteps from "./LoadingSteps";
import { useToast } from "components/Toaster/index";

const YsentinalOne = ({
  compress,
  history,
  setHistory,
  viewMode: externalViewMode,
  setViewMode: setExternalViewMode,
  isHistoryOpen,
  setIsHistoryOpen,
  profile
}) => {
  const [csvData, setCsvData] = useState([]);
  const [suggestedCharts, setSuggestedCharts] = useState([]);
  const [selectedCharts, setSelectedCharts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [internalViewMode, setInternalViewMode] = useState("table");

  const viewMode =
    typeof externalViewMode !== "undefined" ? externalViewMode : internalViewMode;
  const setViewMode = (v) => {
    if (typeof setExternalViewMode === "function") setExternalViewMode(v);
    else setInternalViewMode(v);
  };

  const theme = useTheme();
  const chartRefs = useRef({});
  const activeRequestRef = useRef(0);
  const [lastAddedChart, setLastAddedChart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchProgress, setFetchProgress] = useState(null);
  const showToast = useToast();
  // const [isHistoryOpen, setIsHistoryOpen] = useState(true); // Moved to props
  const isMobile = useScreenMobile({ size: 992 });
  const [activeQuery, setActiveQuery] = useState(null);

  // Dispatch event to notify Homepage when table data changes
  useEffect(() => {
    const hasData = csvData && Array.isArray(csvData) && csvData.length > 0;

    window.dispatchEvent(
      new CustomEvent("ysentinelDataUpdate", {
        detail: { hasData },
      })
    );

    // Optional: Log for debugging
    console.log(
      "Ysentinel data update dispatched, hasData:",
      hasData,
      "rows:",
      csvData?.length || 0
    );
  }, [csvData]);

  // Cleanup: notify that there's no data when component unmounts
  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent("ysentinelDataUpdate", {
          detail: { hasData: false },
        })
      );
    };
  }, []);

  const handleSearch = async (query, options = {}) => {
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;

    try {
      setLoading(true);
      setActiveQuery(query);
      setCsvData([]);
      setSuggestedCharts([]);
      setSelectedCharts([]);
      setFetchProgress({
        stage: "connecting",
        pagesFetched: 0,
        rowsFetched: 0,
        lastBatchSize: 0,
        bytesFetched: 0,
        limitReached: false,
      });

      const [allJsonRows, grammarObjects] = await sendQuery(query, {
        pageSize: 1000,
        maxRows: 20000,
        onProgress: (progress) => {
          if (activeRequestRef.current !== requestId) return;
          setFetchProgress(progress);
        },
        onBatch: (batch) => {
          if (activeRequestRef.current !== requestId) return;

          // Grammar objects come in the first response (may have no rows yet)
          if (batch.grammarObjects && batch.grammarObjects.length > 0) {
            const charts = batch.grammarObjects.map((grammar, idx) => ({
              id: idx,
              name: grammar.description || "Vega Chart",
              category: grammar.mark || "General",
              icon: grammar.icon || "📊",
              type: grammar.mark || "chart",
              spec: grammar,
            }));
            setSuggestedCharts(charts);
          }

          const parsedRows = batch.rows
            .map((row) => JSON.parse(row))
            .filter((row) => row !== null && row !== undefined);

          if (parsedRows.length > 0) {
            setCsvData((prev) => [...prev, ...parsedRows]);
            setLoading(false); // hide LoadingSteps the moment first rows arrive
          }
        },
      });

      if (activeRequestRef.current !== requestId) return;

      if (allJsonRows.length === 0) {
        showToast("Received data is empty");
        return;
      }

      if (!options.skipHistory) {
        setHistory((prev) => {
          const filtered = prev.filter(
            (item) => item.query.trim().toLowerCase() !== query.trim().toLowerCase()
          );
          return [...filtered, { query }];
        });
      }
    } catch (error) {
      if (activeRequestRef.current !== requestId) return;
      showToast("Received data is empty");
    } finally {
      if (activeRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const getFetchProgressLabel = () => {
    if (!fetchProgress) return "";
    if (fetchProgress.stage === "connecting") {
      return "Connecting to log service...";
    }
    if (fetchProgress.stage === "completed") {
      return `Loaded ${fetchProgress.rowsFetched.toLocaleString()} rows.`;
    }
    if (fetchProgress.limitReached) {
      return `Loaded ${fetchProgress.rowsFetched.toLocaleString()} rows.`;
    }
    return `Fetched ${fetchProgress.rowsFetched.toLocaleString()} rows so far...`;
  };

  const isFetchingPages = fetchProgress && fetchProgress.stage !== "completed";

  const handleDelete = (index) => {
    setHistory((prev) => {
      const entryToDelete = prev[index];

      if (entryToDelete?.query === activeQuery) {
        setCsvData([]);
        setSuggestedCharts([]);
        setSelectedCharts([]);
        setActiveQuery(null);
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRestoreHistory = (entry) => {
    handleSearch(entry.query, { skipHistory: true });
  };

  const handleChartSelect = (chartObj) => {
    if (!selectedCharts.some((c) => c.id === chartObj.id)) {
      setSelectedCharts((prev) => [...prev, chartObj]);
      setLastAddedChart(chartObj.id);
    }
  };

  // Auto-close history sidebar on mobile by default when entering the section
  useEffect(() => {
    if (isMobile) {
      setIsHistoryOpen(false);
    }
  }, [isMobile]);

  const handleSearchChange = (searchTerm, filters) => {
    const hasSearchTerm = searchTerm && searchTerm.trim() !== "";
    const hasFilters =
      filters &&
      Object.keys(filters).some(
        (key) => filters[key] && filters[key].trim() !== ""
      );
    setIsSearching(hasSearchTerm || hasFilters);
  };

  useEffect(() => {
    if (lastAddedChart === null || lastAddedChart === undefined) return;
    let attempts = 0;
    const maxAttempts = 10;
    const interval = setInterval(() => {
      const node = chartRefs.current[lastAddedChart];
      attempts += 1;
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
        clearInterval(interval);
        setLastAddedChart(null);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        setLastAddedChart(null);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [lastAddedChart]);

  // Auto-populate selected charts when user switches to charts view
  useEffect(() => {
    if (
      viewMode === "charts" &&
      selectedCharts.length === 0 &&
      suggestedCharts.length > 0
    ) {
      setSelectedCharts(
        suggestedCharts.slice(0, Math.min(6, suggestedCharts.length))
      );
    }
  }, [viewMode, suggestedCharts, selectedCharts, setSelectedCharts]);

  // Handle download requests (PNG) from header
  useEffect(() => {
    const onDownloadCharts = (event) => {
      const { format } = event && event.detail ? event.detail : { format: "csv" };
      if (format !== "png") return;

      if (viewMode !== "charts") {
        showToast("PNG export available only in Charts view", "warning");
        return;
      }

      const chartsToExport =
        selectedCharts && selectedCharts.length > 0
          ? selectedCharts
          : suggestedCharts || [];
      if (chartsToExport.length === 0) {
        showToast("No charts available to export", "warning");
        return;
      }

      const toPngFromElement = async (el) => {
        // Prefer canvas if present
        const canvas = el.querySelector("canvas");
        if (canvas && typeof canvas.toDataURL === "function") {
          return canvas.toDataURL("image/png");
        }

        // Fallback to SVG serialization
        const svg = el.querySelector("svg");
        if (!svg) return null;

        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const svg64 = btoa(unescape(encodeURIComponent(svgString)));
        const b64Start = "data:image/svg+xml;base64,";
        const image64 = b64Start + svg64;

        return await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const canvas2 = document.createElement("canvas");
            canvas2.width = img.width;
            canvas2.height = img.height;
            const ctx = canvas2.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas2.toDataURL("image/png"));
          };
          img.onerror = () => resolve(null);
          img.src = image64;
        });
      };

      (async () => {
        let exported = 0;
        for (const chart of chartsToExport) {
          const node = chartRefs?.current?.[chart.id];
          if (!node) continue;
          const url = await toPngFromElement(node);
          if (!url) continue;

          const link = document.createElement("a");
          link.href = url;
          const safeName = (chart.name || "chart").replace(/[^a-z0-9-_.]/gi, "_");
          link.download = `${safeName}-${chart.id}.png`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          exported += 1;
          await new Promise((r) => setTimeout(r, 250));
        }

        if (exported > 0)
          showToast(`${exported} chart image(s) downloaded`, "success");
        else showToast("Could not export chart images", "error");
      })();
    };

    document.addEventListener("downloadCharts", onDownloadCharts);
    return () => document.removeEventListener("downloadCharts", onDownloadCharts);
  }, [csvData, viewMode, showToast, selectedCharts, suggestedCharts]);

  return (
    <section
      className={isMobile ? "YsentinelOneWrapper" : "YsentinalOneWrapper ysentinel-analysis"}
      style={{ width: isMobile ? "100%" : compress ? "95vw" : "86vw" }}
    >
      <div
        className="ys-inner-wrapper"
        style={{
          backgroundColor:
            theme === "light" ? "#F8F2FD" : "var(--background-bg-dark)",
        }}
      >
        <div
          className={`${isMobile ? "YsentinelContainer" : "YsentinalContainer"} ${isHistoryOpen ? "is-history-open" : ""} ${viewMode === "charts" ? "charts-view-layout" : ""}`}
        >
          {loading ? (
            <div className="layout-loader">
              <LoadingSteps query={activeQuery} />
            </div>
          ) : (
            <>
              {csvData && csvData.length > 0 ? (
                <div className="ys-title" style={{ display: "none" }}>
                  {/* Hidden when data is present */}
                </div>
              ) : (
                <div className="ys-empty-hero">
                  <h2>Ysentinel Analysis</h2>
                  <p>Type your query below to fetch logs and visualize insights.</p>
                </div>
              )}

              {csvData && csvData.length > 0 && (
                <>
                  {viewMode === "table" ? (
                    <div className="table-section">
                      <div className="ysentinel-table-scroll">
                        <TableViews
                          data={csvData}
                          onSearchChange={handleSearchChange}
                          suggestedCharts={suggestedCharts}
                          onChartSelect={handleChartSelect}
                          isHistoryOpen={isHistoryOpen}
                          datasetKey={activeQuery || ""}
                        />
                      </div>
                    </div>
                  ) : (
                    Array.isArray(suggestedCharts) &&
                    suggestedCharts.length > 0 &&
                    !isSearching && (
                      // themed wrapper so ChartDashboard can react to .theme-dark / .theme-light
                      <div
                        className={
                          "charts-section YsentinalChartsSection sidebar-aware-content " +
                          (isHistoryOpen ? " " : "sidebar-closed") +
                          (theme === "dark" ? " theme-dark" : " theme-light")
                        }
                      >
                        <ChartDashboard
                          suggestedCharts={suggestedCharts}
                          selectedCharts={selectedCharts}
                          csvData={csvData}
                          chartRefs={chartRefs}
                          isHistoryOpen={isHistoryOpen}
                        />
                      </div>
                    )
                  )}
                </>
              )}
            </>
          )}

          <LogHistorySidebar
            history={history}
            handleDelete={handleDelete}
            handleClick={handleRestoreHistory}
            activeQuery={activeQuery}
            onToggle={setIsHistoryOpen}
            isOpen={isHistoryOpen}
          />

          {/* themed wrapper so FooterSearchBar2 can react to .theme-dark / .theme-light */}
          {viewMode !== "charts" && (
            <div
              className={`ys-footer-wrapper ${theme === "dark" ? "theme-dark" : "theme-light"}`}
              style={{ marginTop: "auto" }}
            >
              {fetchProgress && (
                <div className="ys-fetch-progress-strip">
                  <div className="ys-fetch-progress-copy">
                    <span className={`ys-fetch-progress-dot ${isFetchingPages ? "is-loading" : "is-done"}`} />
                    <span>{getFetchProgressLabel()}</span>
                  </div>
                  {isFetchingPages && fetchProgress.stage === "fetching" && (
                    <span className="ys-fetch-progress-batch">
                      +{fetchProgress.lastBatchSize} rows in last batch
                    </span>
                  )}
                </div>
              )}
              <FooterSearchBar2
                onSearch={handleSearch}
                isHistoryOpen={isHistoryOpen}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default YsentinalOne;
